import { toPrimitiveBandPosition } from '@/adapters/primitive/_pure/derivation/constellations';
import { buildFlightWheel } from '@/adapters/primitive/_pure/derivation/wheels';
import type {
    Config,
    PrimitiveWheelRole,
    PrimitiveWheelState,
    SettlementPreviewConfig,
} from '@/adapters/primitive/_contract/config';
import type { NaturalSettlementBandCells } from '@/adapters/primitive/_pure/model/natural/settlementTrace';

type WheelIdentity = { role: PrimitiveWheelRole; variant?: PrimitiveWheelState['variant'] };

export type SettlementPreview = {
    settlements: readonly NaturalSettlementBandCells[];
    incoming: (index: number, power: number) => WheelIdentity;
    /** A receiver that is itself a rotated Contribution-traveller maps receiver slots back to its own digits. */
    receiver: (power: number) => WheelIdentity & { slotOffset?: number };
};

function findWheel(
    wheels: readonly PrimitiveWheelState[],
    identity: WheelIdentity,
    power: number,
): PrimitiveWheelState | undefined {
    return wheels.find((wheel) => wheel.role === identity.role
        && wheel.variant === identity.variant
        && wheel.powers.includes(power));
}

/** A preview needs a real receiver to frame against; placeholders simply materialize the incoming band. */
export function isSettlementPreviewShown(args: {
    config: Config;
    wheels: readonly PrimitiveWheelState[];
    preview: SettlementPreview;
}): boolean {
    if (!args.config.natural.display.settlementPreview.visible || args.preview.settlements.length === 0) return false;
    return args.preview.settlements.every((settlement) => {
        const identity = args.preview.receiver(settlement.power);
        const receiver = args.wheels.find((wheel) => wheel.role === identity.role && wheel.variant === identity.variant);
        return receiver !== undefined && receiver.valueKind !== 'placeholder';
    });
}

/** Which receiver slots have resolved: none yet (`entry`), all, or the given ones. */
export type SettlementResolution = 'entry' | 'all' | ReadonlySet<number>;

/** Timed resolution frames: one for all slots, or one per receiver slot. */
export function toSettlementResolutionSchedule(args: {
    config: SettlementPreviewConfig;
    preview: SettlementPreview;
}): { resolution: SettlementResolution; durationMs: number }[] {
    const { config } = args;
    if (config.sequencingMode === 'parallel') return [{ resolution: 'all', durationMs: config.resolveDurationMs }];
    // Every band resolves the same slot together.
    const slots = [...new Set(args.preview.settlements.flatMap((settlement) => (
        settlement.cells.map((cell) => cell.targetSlot)
    )))].sort((left, right) => left - right);
    return slots.map((_, index) => ({
        resolution: new Set(slots.slice(0, index + 1)),
        durationMs: config.slotDurationMs,
    }));
}

type CellOverride = { offset?: number; opacity?: number };

/**
 * Resolves the two participants into the settled band spread over three depths: each slot's winner stays at its own
 * depth, the loser travels onto it and fades, and a draw fades both into an uncounted cell at the ante between them.
 */
export function withSettlementResolution(args: {
    config: Config;
    wheels: readonly PrimitiveWheelState[];
    preview: SettlementPreview;
    resolution: SettlementResolution;
}): PrimitiveWheelState[] {
    if (!isSettlementPreviewShown(args)) return [...args.wheels];
    const radix = args.config.natural.radix;
    const { resolution } = args;
    const isResolved = (slot: number) => resolution === 'all' || (resolution !== 'entry' && resolution.has(slot));
    const anyResolved = resolution === 'all' || (resolution !== 'entry' && resolution.size > 0);
    const overrides = new Map<PrimitiveWheelState, Record<number, Record<number, CellOverride>>>();
    const override = (wheel: PrimitiveWheelState, power: number, digit: number, value: CellOverride) => {
        const byPower = overrides.get(wheel) ?? {};
        byPower[power] = { ...byPower[power], [digit]: value };
        overrides.set(wheel, byPower);
    };
    const ghostPowers = new Map<PrimitiveWheelState, number[]>();
    const anteWheels: PrimitiveWheelState[] = [];
    const participants = new Map<PrimitiveWheelState, { participant: 'source' | 'target'; powers: number[] }>();
    const participate = (wheel: PrimitiveWheelState, participant: 'source' | 'target', power: number) => {
        const entry = participants.get(wheel) ?? { participant, powers: [] };
        if (!entry.powers.includes(power)) entry.powers.push(power);
        participants.set(wheel, entry);
    };

    args.preview.settlements.forEach((settlement, index) => {
        const { power, cells } = settlement;
        const receiver = args.preview.receiver(power);
        const source = findWheel(args.wheels, args.preview.incoming(index, power), power);
        const target = args.wheels.find((wheel) => wheel.role === receiver.role && wheel.variant === receiver.variant);
        if (source === undefined || target === undefined) return;
        participate(source, 'source', power);
        participate(target, 'target', power);
        const toTargetDigit = (slot: number) => (((slot - (receiver.slotOffset ?? 0)) % radix) + radix) % radix;
        const hasTargetBand = target.powers.includes(power);
        const sourcePosition = toPrimitiveBandPosition(source, power);
        const targetPosition = hasTargetBand ? toPrimitiveBandPosition(target, power) : target.position;
        // Cell offsets run along the wheel axis, which is primitive depth.
        const towardSource = sourcePosition[2] - targetPosition[2];

        if (!hasTargetBand) {
            // The machine adds this band on settlement; showing it empty gives losing cells somewhere to land.
            ghostPowers.set(target, [...(ghostPowers.get(target) ?? []), power]);
            for (let digit = 0; digit < radix; digit += 1) {
                override(target, power, digit, { opacity: anyResolved ? 1 : 0 });
            }
        }

        const drawDigits: number[] = [];
        for (const cell of cells) {
            const targetDigit = toTargetDigit(cell.targetSlot);
            if (cell.outcome === 'cancelled') drawDigits.push(targetDigit);
            if (!isResolved(cell.targetSlot)) continue;
            if (cell.outcome === 'added') {
                override(target, power, targetDigit, { offset: towardSource, opacity: 0 });
            } else if (cell.outcome === 'cancelled') {
                override(source, power, cell.digit, { offset: -towardSource / 2, opacity: 0 });
                override(target, power, targetDigit, { offset: towardSource / 2, opacity: 0 });
            } else {
                override(source, power, cell.digit, { offset: -towardSource, opacity: 0 });
            }
        }

        if (drawDigits.length === 0) return;
        const resolvedDraws = new Set(cells
            .filter((cell) => cell.outcome === 'cancelled' && isResolved(cell.targetSlot))
            .map((cell) => toTargetDigit(cell.targetSlot)));
        anteWheels.push({
            ...buildFlightWheel({
                role: target.role,
                variant: 'ante',
                value: 0,
                power,
                position: [
                    (sourcePosition[0] + targetPosition[0]) / 2,
                    (sourcePosition[1] + targetPosition[1]) / 2,
                    (sourcePosition[2] + targetPosition[2]) / 2,
                ],
                rotation: target.rotationByPower?.[power] ?? 0,
                countedDigits: [],
            }),
            cellOverridesByPower: {
                [power]: Object.fromEntries(Array.from({ length: radix }, (_, digit) => [
                    digit,
                    { opacity: resolvedDraws.has(digit) ? 1 : 0 },
                ])),
            },
        });
    });

    // Stable across the entry, resolve and seat frames of one preview, so the enclosing wall tweens rather than pops.
    const target = [...participants].find(([, entry]) => entry.participant === 'target')?.[0];
    const unitKey = target === undefined
        ? undefined
        : [
            target.role,
            target.variant ?? 'base',
            ...[...new Set([...participants.values()].flatMap((entry) => entry.powers))].sort((left, right) => left - right),
        ].join(':');

    return [
        ...args.wheels.map((wheel) => {
            const byPower = overrides.get(wheel);
            const ghosts = ghostPowers.get(wheel) ?? [];
            const participation = participants.get(wheel);
            if (byPower === undefined && ghosts.length === 0 && participation === undefined) return wheel;
            return {
                ...wheel,
                ...(ghosts.length === 0 ? {} : {
                    powers: [...wheel.powers, ...ghosts].sort((left, right) => left - right),
                    countedThroughDigitByPower: {
                        ...wheel.countedThroughDigitByPower,
                        ...Object.fromEntries(ghosts.map((power) => [power, 0])),
                    },
                    countedDigitsByPower: {
                        ...wheel.countedDigitsByPower,
                        ...Object.fromEntries(ghosts.map((power) => [power, []])),
                    },
                }),
                ...(byPower === undefined ? {} : { cellOverridesByPower: { ...wheel.cellOverridesByPower, ...byPower } }),
                ...(participation === undefined || unitKey === undefined ? {} : {
                    settlementUnit: {
                        unitKey,
                        participant: participation.participant,
                        powers: [...participation.powers].sort((left, right) => left - right),
                        opacity: anyResolved ? 1 : 0,
                    },
                }),
            };
        }),
        ...anteWheels,
    ];
}
